// Read-only classification labels derived on Tab 1 (Crude Info).
// Thresholds taken verbatim from rule-crude-registration.md — note the rule has
// documented gaps (Sulfur skips 0.2–0.6; Yield uses a `gas - HN > 20` override).
// Kept isolated here so thresholds are trivially adjustable when BE confirms.

// Sulfur Classification from the Sulfur (%-WT) input.
//   0   – 0.1  → Sweet
//   0.1 – 0.2  → Light Sour
//   0.6 – 1.9  → Medium Sour
//   >= 2        → Heavy Sour
// Values in the undefined gaps (0.2–0.6, 1.9–2) return '' (blank, like the mock).
export const deriveSulfurClass = sulfur => {
  const s = parseFloat(sulfur);
  if (isNaN(s)) return '';
  if (s >= 0 && s <= 0.1) return 'Sweet';
  if (s > 0.1 && s <= 0.2) return 'Light Sour';
  if (s >= 0.6 && s <= 1.9) return 'Medium Sour';
  if (s >= 2) return 'Heavy Sour';
  return '';
};

// Yield Classification from Total Yield, with a Gas/H-Naphta override.
//   0    – 96.7  → Medium
//   96.8 – 124   → Heavy
//   >= 125        → Super Heavy
//   override: if (gas - hNaphta) > 20 → Light (takes precedence)
export const deriveYieldClass = (totalYield, gas, hNaphta) => {
  const t = parseFloat(totalYield);
  const g = parseFloat(gas);
  const hn = parseFloat(hNaphta);
  if (!isNaN(g) && !isNaN(hn) && g - hn > 20) return 'Light';
  if (isNaN(t)) return '';
  if (t >= 0 && t <= 96.7) return 'Medium';
  if (t >= 96.8 && t <= 124) return 'Heavy';
  if (t >= 125) return 'Super Heavy';
  return '';
};

// The API deserializes ClassificationSulfur/ClassificationYield as the enum's
// NUMERIC value (not a string member name — the backend rejects strings with
// "The JSON value could not be converted to ... YieldClassification"). So the
// display labels above are mapped to their enum ints before submit
// (api-crude-registration.md — Sulfur: Sweet=1/LightSour=2/MediumSour=3/
// HeavySour=4; Yield: Light=1/Medium=2/Heavy=3/SuperHeavy=4). Blank label →
// null (field omitted).
const SULFUR_ENUM = {
  Sweet: 1,
  'Light Sour': 2,
  'Medium Sour': 3,
  'Heavy Sour': 4,
};
const YIELD_ENUM = {
  Light: 1,
  Medium: 2,
  Heavy: 3,
  'Super Heavy': 4,
};

// Also accept an already-stored enum member NAME (GetCrude returns these) so
// classificationYieldBefore normalizes whether it arrives as a label or a name.
const YIELD_NAME_TO_INT = {
  Light: 1,
  Medium: 2,
  Heavy: 3,
  SuperHeavy: 4,
};

// Enum int for the Sulfur Classification (or null when undetermined).
export const deriveSulfurClassEnum = sulfur => {
  const v = SULFUR_ENUM[deriveSulfurClass(sulfur)];
  return v ?? null;
};

// Enum int for the Yield Classification (or null when undetermined).
export const deriveYieldClassEnum = (totalYield, gas, hNaphta) => {
  const v = YIELD_ENUM[deriveYieldClass(totalYield, gas, hNaphta)];
  return v ?? null;
};

// Normalize a stored Yield value (display label OR enum member name OR int) to
// its enum int (used for classificationYieldBefore in the single-shot §5 flow).
export const yieldLabelToEnum = value => {
  if (typeof value === 'number') return value;
  return YIELD_ENUM[value] ?? YIELD_NAME_TO_INT[value] ?? null;
};

// Reverse of YIELD_NAME_TO_INT — a stored int yield normalized to its member name.
const YIELD_INT_TO_NAME = {
  1: 'Light',
  2: 'Medium',
  3: 'Heavy',
  4: 'SuperHeavy',
};

// Normalize a stored Yield value (display label OR enum member name OR int) to its
// enum member NAME string. Used for classificationYieldBefore in the Draft edit
// flow (UA1/UA2), which serializes enums as string member names. Mirrors
// yieldLabelToEnum, which returns the int for the single-shot §5 flow.
export const yieldLabelToName = value => {
  if (typeof value === 'number') return YIELD_INT_TO_NAME[value] ?? null;
  // Already a member name (Light/Medium/Heavy/SuperHeavy) → pass through.
  if (Object.values(YIELD_INT_TO_NAME).includes(value)) return value;
  // Spaced display label ("Super Heavy") → member name.
  return YIELD_NAME[value] ?? null;
};

// The Draft flow (D1–D6) serializes these enums as their C# member NAME string
// (not the int — that's only for the old single-shot §2/4/5 endpoints). Map the
// spaced display labels to the space-free member names the backend expects.
const SULFUR_NAME = {
  Sweet: 'Sweet',
  'Light Sour': 'LightSour',
  'Medium Sour': 'MediumSour',
  'Heavy Sour': 'HeavySour',
};
const YIELD_NAME = {
  Light: 'Light',
  Medium: 'Medium',
  Heavy: 'Heavy',
  'Super Heavy': 'SuperHeavy',
};

// Enum member name for the Sulfur Classification (or null when undetermined).
export const deriveSulfurClassName = sulfur =>
  SULFUR_NAME[deriveSulfurClass(sulfur)] ?? null;

// Enum member name for the Yield Classification (or null when undetermined).
export const deriveYieldClassName = (totalYield, gas, hNaphta) =>
  YIELD_NAME[deriveYieldClass(totalYield, gas, hNaphta)] ?? null;

// Sum of the seven Yield fields → Total Yield (read-only). Missing/blank inputs
// count as 0 so the running total updates as fields are filled.
export const computeTotalYield = form => {
  const nums = [
    'gas',
    'lNaphta',
    'hNaphta',
    'kerosene',
    'lGasoil',
    'hGasoil',
    'residu',
  ].map(k => parseFloat(form[k]) || 0);
  const total = nums.reduce((a, b) => a + b, 0);
  // Guard float drift (e.g. 99.99999999) so the "= 100" check is stable.
  return Math.round(total * 100) / 100;
};
